import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PRZEBIEG UZGADNIAJĄCY — kontrakt ekranu z portalem.
 *
 *  ⚠️⚠️ LISTA TABEL I NUMERY AUTOMATÓW PRZYCHODZĄ Z SERWERA, a nie stoją tutaj. To portal wie, dla
 *  których tabel zna klucz naturalny i które opisują jeden automat — druga kopia tej wiedzy
 *  w kliencie rozjechałaby się po cichu przy pierwszej nowej tabeli, a rozjazd siedziałby w tym,
 *  czy zapytanie do bazy klienta w ogóle się wykona.
 *
 *  ⚠️ SCHEMAT NIE MOŻE BYĆ OSTRZEJSZY NIŻ ODPOWIEDŹ SERWISU — patrz `api.ts`. Pole wymagane tutaj,
 *  a nieoddawane tam, zamienia UDANY zapis w czerwony komunikat o błędzie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export const PrzebiegSchema = z.object({
  id: z.string(),
  tabela: z.string(),
  vmid: z.number().nullable(),
  stan: z.enum(['TRWA', 'WSTRZYMANY', 'PRZERWANY', 'ZROBIONY']),
  faza: z.enum(['GOTOWY', 'ODCZYT', 'ROZSYLKA']),
  /** Węzeł źródłowy TERAZ; `null` = kursor wyszedł za koniec listy. */
  wezel: z.string().nullable(),
  wezlow: z.number(),
  wezelNr: z.number(),
  od: z.number(),
  wielkoscPaczki: z.number(),
  paczek: z.number(),
  wierszyPrzejrzanych: z.number(),
  wierszyRozeslanych: z.number(),
  pominietychZnanych: z.number(),
  /** Węzły, które NIE były źródłem — bez tej listy „zrobiony" kłamie. */
  pominieteWezly: z.array(z.string()),
  powodPauzy: z.string().nullable(),
  blokujacyWezel: z.string().nullable(),
  zaczety: z.string(),
  domkniety: z.string().nullable(),
});

export type Przebieg = z.infer<typeof PrzebiegSchema>;

export const fetchPrzebiegi = (groupId: string) =>
  apiGet(`/uzgadnianie?groupId=${encodeURIComponent(groupId)}`, z.array(PrzebiegSchema));

export const fetchTabeleDoUzgodnienia = (groupId: string) =>
  apiGet(
    `/uzgadnianie/tabele?groupId=${encodeURIComponent(groupId)}`,
    z.object({
      tabele: z.array(z.object({ sps: z.string(), wyspowa: z.boolean() })),
      automaty: z.array(z.object({ vmid: z.number(), nazwa: z.string() })),
    }),
  );

/**
 * CO ZROBI „ZACZNIJ" — zdanie liczone przez portal, bez skutku.
 *
 * ⚠️⚠️ ZDANIE SKŁADA SERWER. Tylko on wie, ile węzłów wejdzie do przebiegu i które odpadną jako
 * za stare — a zapowiedź opisująca inny zestaw niż ten, który potem ruszy, jest gorsza niż brak
 * zapowiedzi.
 */
export const podgladPrzebiegu = (groupId: string, tabela: string, vmid: number | null) =>
  apiGet(
    `/uzgadnianie/podglad?groupId=${encodeURIComponent(groupId)}&tabela=${encodeURIComponent(tabela)}` +
      (vmid === null ? '' : `&vmid=${vmid}`),
    z.object({
      mozliwe: z.boolean(),
      /** `null` = da się; napis = powód i co z nim zrobić. */
      powod: z.string().nullable(),
      wezly: z.array(z.string()),
      zaStare: z.array(z.string()),
      opis: z.string(),
    }),
  );

export const zacznijPrzebieg = (groupId: string, tabela: string, vmid: number | null) =>
  apiSend(
    'POST',
    '/uzgadnianie/zacznij',
    { groupId, tabela, vmid },
    z.object({ id: z.string(), wezly: z.array(z.string()), zaStare: z.array(z.string()) }),
  );

export const przerwijPrzebieg = (groupId: string, id: string) =>
  apiSend('POST', '/uzgadnianie/przerwij', { groupId, id }, z.object({ stan: z.string() }));

export const wznowPrzebieg = (groupId: string, id: string) =>
  apiSend('POST', '/uzgadnianie/wznow', { groupId, id }, z.object({ stan: z.string() }));
